/* frameworks and libraries first */
import Lenis from 'lenis';

/* custom components second */
import './components/nav';
import './components/waveDivider';
import './components/shaderBg';
import './components/hero3dBg';

import { ScrollEngine } from './scroll/engine';
import type { ShaderBg } from './components/shaderBg';
import type { Hero3dBg } from './components/hero3dBg';


class AppShell {

    private lenis: Lenis | null = null;
    private engine = new ScrollEngine();
    private shaders: ShaderBg[] = [];
    private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    constructor() {
        this.init();
    }

    private init() {
        this.initSmoothScroll();
        this.initScrollEffects();
        this.initReveals();
        this.initIntro();

        // every in-page anchor (nav, footer, CTAs) rides the Lenis easing
        document.addEventListener('click', (e) => {
            const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
            if (!link || link.getAttribute('href') === '#') return;
            e.preventDefault();
            this.scrollTo(link.getAttribute('href')!);
        });

        // this allows components to safely invoke exposed functions
        (window as any).App = this;
    }

    /* nav + anchors ride through here so everything shares the same easing */
    public scrollTo(target: string) {
        if (this.lenis) {
            this.lenis.scrollTo(target, { duration: 1.4 });
        } else {
            document.querySelector(target)?.scrollIntoView();
        }
    }

    private initSmoothScroll() {
        if (!this.reducedMotion) {
            this.lenis = new Lenis({
                duration: 1.2,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                orientation: 'vertical',
                smoothWheel: true
            });
        }

        const scrollLoop = (time: number) => {
            this.lenis?.raf(time);

            const y = this.lenis ? this.lenis.scroll : window.scrollY;
            const v = this.lenis ? this.lenis.velocity : 0;

            this.engine.tick(y, v);
            for (const s of this.shaders) s.setVelocity(v);

            requestAnimationFrame(scrollLoop);
        }
        requestAnimationFrame(scrollLoop);
    }

    /* register every scroll-driven element with the engine */
    private initScrollEffects() {

        const hero = document.querySelector<HTMLElement>('.hero');
        const heroBg = hero?.querySelector<Hero3dBg>('hero-3d-bg') ?? null;
        if (hero) {
            this.engine.add(hero, {
                mode: 'view',
                onUpdate: () => {
                    const scrollY = this.scrollY();
                    const vh = window.innerHeight;
                    
                    // 500px of scroll pins the scene and drives rotation
                    const rotationProgress = Math.max(0.0, Math.min(1.0, scrollY / 500));
                    heroBg?.setProgress(rotationProgress);

                    // exitProgress starts after the 500px pin and runs for 1 viewport height
                    const exitProgress = Math.max(0.0, Math.min(1.0, (scrollY - 500) / vh));
                    hero.style.setProperty('--exit', exitProgress.toFixed(4));
                },
            });
        }

        // wave divider morphs on scroll progress and wobbles on scroll velocity
        const wave = document.querySelector<any>('wave-divider');
        if (wave) {
            this.engine.add(wave, {
                mode: 'view',
                onUpdate: (p) => {
                    const velocity = this.lenis ? this.lenis.velocity : 0;
                    wave.updateProgress(p, velocity);
                }
            });
        }

        // what I build: pinned stage (desktop + mobile); vertical scroll scrubs
        // --p, which drives the titles and UI clusters scrolling through
        const build = document.querySelector<HTMLElement>('.build');
        if (build) {
            this.engine.add(build, { mode: 'scrub' });
        }

        // From vibe to prod: pinned stage vertical scroll scrub with 0.6 ratio
        const prod = document.querySelector<HTMLElement>('.prod');
        if (prod) {
            this.engine.add(prod, {
                mode: 'scrub',
                ratio: 0.6,
                when: () => !this.reducedMotion && window.innerWidth >= 900
            });
        }

        // Tools of the trade: pinned stage vertical scroll scrub with 0.6 ratio
        const stack = document.querySelector<HTMLElement>('.stack');
        if (stack) {
            this.engine.add(stack, {
                mode: 'scrub',
                ratio: 0.6,
                when: () => !this.reducedMotion && window.innerWidth >= 900
            });
        }

        // capabilities: section progress drives the paint field under the glyphs
        const forge = document.querySelector<HTMLElement>('.forge');
        const forgeShader = forge?.querySelector<ShaderBg>('shader-bg') ?? null;
        if (forge) {
            this.engine.add(forge, {
                mode: 'view',
                onUpdate: (p) => forgeShader?.setProgress(p),
            });
        }
        if (forgeShader) this.shaders.push(forgeShader);

        // progress spine + big-type scale-ins
        document.querySelectorAll<HTMLElement>('[data-scrub]').forEach((el) => {
            this.engine.add(el, { mode: 'view' });
        });

        this.engine.refresh(window.scrollY);
        this.measureForgeSafe();

        // layout shifts when fonts swap; pin widths shift on resize
        document.fonts.ready.then(() => {
            this.engine.refresh(this.scrollY());
            this.measureForgeSafe();
        });

        let timer = 0;
        window.addEventListener('resize', () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                this.engine.refresh(this.scrollY());
                this.measureForgeSafe();
            }, 150);
        });
    }

    /* reserve the *measured* nav height (so it tracks the pill's padding) plus a
     * --space-lg margin as the forge's top clear-band, then let the glyph shader
     * re-lay-out the DESIGN/BUILD/SHIP/SCALE block beneath it */
    private measureForgeSafe() {
        const pill = document.querySelector<HTMLElement>('.nav-pill');
        if (!pill) return;
        // the pill hangs half over the subheader band: its bottom edge sits at
        // --subheader-h + half its own height (offsetHeight is transform-proof)
        const navBottom = this.cssPx('var(--subheader-h)') + pill.offsetHeight / 2;
        const margin = this.cssPx('var(--space-lg)');
        // both reserves are resolved to px here and owned on :root — the glyph
        // shader reads them with parseFloat, which can't resolve clamp()/vh
        const root = document.documentElement.style;
        root.setProperty('--forge-top-safe', `${Math.round(navBottom + margin)}px`);
        root.setProperty('--forge-bottom-safe', `${Math.round(this.cssPx('clamp(4rem, 11vh, 8rem)'))}px`);
        for (const s of this.shaders) s.reflow();
    }

    /* resolve a CSS length (clamp / vh / var, …) to pixels via a throwaway probe */
    private cssPx(value: string): number {
        const probe = document.createElement('div');
        probe.style.cssText = `position:absolute;left:-9999px;top:0;height:${value};`;
        document.body.appendChild(probe);
        const px = probe.getBoundingClientRect().height;
        probe.remove();
        return px;
    }

    /* one-shot entrance reveals for copy blocks */
    private initReveals() {
        const io = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                entry.target.classList.add('in');
                io.unobserve(entry.target);
            }
        }, { threshold: 0.25 });

        document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));
    }

    /* hold the hero lines until fonts are in, then let them rise */
    private initIntro() {
        const go = () => document.body.classList.add('is-ready');
        document.fonts.ready.then(go);
        window.setTimeout(go, 900);   // fonts are local; this is just a safety net
    }

    private scrollY(): number {
        return this.lenis ? this.lenis.scroll : window.scrollY;
    }
}

window.addEventListener('DOMContentLoaded', () => {
    new AppShell();
})
